import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

export default function WidgetView({
    widget,
    t,
    color,
    textButtonColor = '#FFFFFF',
    onClick,
    readonly = false,
    scaleFactor = 1,
}) {
    const [embeddableVideoUrl, setEmbeddableVideoUrl] = useState(undefined);
    const stackRef = useRef(null);
    const [height, setHeight] = useState(null);

    const aspectRatio = 540 / 236;

    useEffect(() => {
        const updateHeight = () => {
            if (stackRef.current) {
                const width = stackRef.current.offsetWidth;
                const calculatedHeight = width / aspectRatio;
                setHeight(calculatedHeight);
            }
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const handleDownload = () => {
        if (onClick) onClick(widget);
        const d = document.createElement('a');
        d.href = widget.payload.document.url;
        d.target = '_blank';
        d.click();
    };

    const widgetLinkUrl = widget.payload.link?.url;
    const imgWidget = (
        <img
            style={{
                cursor: widgetLinkUrl ? 'pointer' : 'auto',
                borderRadius: '16px',
                width: '100%',
                transform: `scale(${scaleFactor})`,
                transformOrigin: 'center',
            }}
            src={widget.payload.image?.url}
            alt="img"
        />
    );

    useEffect(() => {
        if (widget?.payload?.type === 'video') {
            const converted = convertToEmbeddableVideoUrl(widget.payload.link.url);

            if (converted) setEmbeddableVideoUrl(converted);
            else setEmbeddableVideoUrl(widget.payload.link.url);
        }
    }, [widget]);

    const hasButton = Boolean(widget.payload.link?.label) || Boolean(widget.payload.document?.url);

    return (
        <>
            <Stack
                ref={stackRef}
                alignItems={'center'}
                sx={{
                    background: '#FFFFFF',
                    width: '100%',
                    maxWidth: '540px',
                    height: height ? `${height}px` : 'auto',
                    boxShadow: 1,
                    borderRadius: '20px',
                    justifyContent: 'center',
                    marginBottom: `${scaleFactor * 20}px`,
                    cursor: !readonly && !hasButton && widget.isLead ? 'pointer' : 'auto',
                }}
                onClick={!readonly && !hasButton ? () => onClick(widget) : undefined}
            >
                {widget.payload.type === 'image' && imgWidget}
                {widget.payload.type === 'text' && (
                    <Stack
                        direction={'column'}
                        alignSelf={'start'}
                        textAlign={'start'}
                        alignContent={'start'}
                        spacing={1 * scaleFactor}
                        pt={1 * scaleFactor}
                        sx={{
                            maxHeight: height,
                            height: '100%',
                            width: '100%',
                            maxWidth: 540,
                            marginBottom: `${scaleFactor * (readonly ? 12 : 24)}px`,
                        }}
                        justifyContent={'space-between'}
                        textOverflow={'ellipsis'}
                    >
                        <span
                            style={{
                                marginLeft: 16 * scaleFactor,
                                marginRight: 16 * scaleFactor,
                                marginTop: readonly * 12 * scaleFactor,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontSize: `${scaleFactor * 18}px !important`,
                                    wordBreak: 'break-word',
                                    lineHeight: 1.2,
                                    marginBottom: 1 * scaleFactor,
                                }}
                            >
                                {widget.payload.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: `${scaleFactor * 14}px !important`,
                                    lineHeight: 1.5,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {widget.payload.body}
                            </Typography>
                        </span>
                        {!readonly && hasButton && (
                            <span>
                                <Button
                                    onClick={() => onClick(widget)}
                                    variant="contained"
                                    sx={{
                                        background: color,
                                        borderColor: color,
                                        color: textButtonColor,
                                        marginLeft: `${scaleFactor * 16}px`,
                                        marginRight: `${scaleFactor * 16}px`,
                                        border: `1px solid ${textButtonColor}`,
                                        '&:hover': {
                                            borderColor: color,
                                            background: color,
                                        },
                                        transform: `scale(${scaleFactor})`,
                                        transformOrigin: 'center',
                                    }}
                                >
                                    {widget.payload.link.label}
                                </Button>
                            </span>
                        )}
                    </Stack>
                )}

                {widget?.payload?.type === 'video' && embeddableVideoUrl && (
                    <iframe
                        width="100%"
                        height={height}
                        src={embeddableVideoUrl}
                        title="Video"
                        style={{ border: 0, borderRadius: 16 }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                )}

                {widget.payload.type === 'file' && (
                    <Stack
                        direction={'column'}
                        textAlign={'start'}
                        alignSelf={'start'}
                        alignContent={'start'}
                        spacing={1 * scaleFactor}
                        pt={1 * scaleFactor}
                        justifyContent={'space-between'}
                        sx={{
                            maxHeight: height,
                            height: '100%',
                            width: '100%',
                            marginBottom: `${scaleFactor * (readonly ? 12 : 24)}px`,
                        }}
                    >
                        <span
                            style={{
                                marginLeft: 16 * scaleFactor,
                                marginRight: 16 * scaleFactor,
                                marginTop: readonly * 12 * scaleFactor,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontSize: `${scaleFactor * 18}px !important`,
                                    wordBreak: 'break-word',
                                    lineHeight: 1.2,
                                    marginBottom: 1 * scaleFactor,
                                }}
                            >
                                {widget.payload.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: `${scaleFactor * 14}px !important`,
                                    lineHeight: 1.5,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {widget.payload.body}
                            </Typography>
                        </span>
                        {!readonly && hasButton && (
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleDownload}
                                    sx={{
                                        background: color,
                                        marginLeft: `${scaleFactor * 16}px`,
                                        marginRight: `${scaleFactor * 16}px`,
                                        color: textButtonColor,
                                        border: `1px solid ${textButtonColor}`,
                                        borderColor: color,
                                        '&:hover': {
                                            borderColor: color,
                                            background: color,
                                        },
                                        transform: `scale(${scaleFactor})`,
                                        transformOrigin: 'center',
                                    }}
                                >
                                    {t('pages.widget.download')}
                                </Button>
                            </span>
                        )}
                    </Stack>
                )}
            </Stack>
        </>
    );
}

export const convertToEmbeddableVideoUrl = (url) => {
    let converted = convertToYoutubeEmbeddableVideoUrl(url);
    if (converted) return converted;
    return convertToVimeoEmbeddableVideoUrl(url);
};

const convertToYoutubeEmbeddableVideoUrl = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);

    return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}` : undefined;
};

const convertToVimeoEmbeddableVideoUrl = (url) => {
    const vimeoRegex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
    const match = url.match(vimeoRegex);
    if (match) {
        const videoId = match[1];
        const hash = match[2] ? `?h=${match[2]}` : '';
        return `https://player.vimeo.com/video/${videoId}${hash}`;
    }
    return undefined;
};
